import type { IconDefinition } from "@fullscript/pro-solid-svg-icons";
import { faListOl, faListUl } from "@fullscript/pro-solid-svg-icons";
import {
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode,
  ListNode,
  INSERT_ORDERED_LIST_COMMAND,
  $getListDepth,
} from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $isHeadingNode } from "@lexical/rich-text";
import { $getNearestNodeOfType, mergeRegister } from "@lexical/utils";
import type { LexicalCommand, LexicalNode } from "lexical";
import {
  COMMAND_PRIORITY_HIGH,
  INDENT_CONTENT_COMMAND,
  $getSelection,
  $isRangeSelection,
} from "lexical";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { l } from "@aviary/locales/i18n";

import { ToolbarButton } from "../ToolbarButton";

const MAX_LIST_DEPTH = 4;

const BlockFormatButtons = () => {
  const { t } = useTranslation();

  const [editor] = useLexicalComposerContext();

  const [blockType, setBlockType] = useState("paragraph");

  // ensures toolbar buttons are in an active state when they should be by looking at the DOM element types in the lexical tree
  const updateToolbar = useCallback(() => {
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element =
        anchorNode.getKey() === "root" ? anchorNode : anchorNode.getTopLevelElementOrThrow();
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);

      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode);
          const type = parentList ? parentList.getTag() : element.getTag();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element) ? element.getTag() : element.getType();
          setBlockType(type);
        }
      }
    }
  }, [editor]);

  // Limits indents when there is no selection
  const isMaxNakedIntent = (anchorNode: LexicalNode) => {
    // oxlint-disable-next-line no-underscore-dangle
    if ((anchorNode as unknown as { __indent: number }).__indent <= MAX_LIST_DEPTH) {
      return false;
    }

    return true;
  };

  // max list indent set at 4
  const isMaxIndent = (): boolean => {
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const parentList = $getNearestNodeOfType(anchorNode, ListNode);

      // Stop normal execution if you do not have any selection highlighted
      // Without this, if you attempt to tab without selected text, RTE
      // will error out due to the lack of a parent list to get depth from
      if (!parentList) {
        return isMaxNakedIntent(anchorNode);
      }

      return $getListDepth(parentList) >= MAX_LIST_DEPTH;
    }

    return false;
  };

  // command to prevent infinite indents in lists
  useEffect(() => {
    return editor.registerCommand(
      INDENT_CONTENT_COMMAND,
      () => isMaxIndent(),
      COMMAND_PRIORITY_HIGH
    );
  }, [editor]);

  // looks/reads the current state of the editor, and calls updateToolbar when the RTE has edits
  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      })
    );
  }, [updateToolbar, editor]);

  const formatList = (isActive: boolean, command: LexicalCommand<void>) => {
    if (!isActive) {
      editor.dispatchCommand(command, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      setBlockType("paragraph");
    }
  };

  const BLOCK_FORMATS: {
    icon: IconDefinition;
    aria: string;
    isActive: boolean;
    command: LexicalCommand<void>;
  }[] = [
    {
      icon: faListUl,
      aria: l.aviary.richTextEditor.unorderedList,
      isActive: blockType === "ul",
      command: INSERT_UNORDERED_LIST_COMMAND,
    },
    {
      icon: faListOl,
      aria: l.aviary.richTextEditor.orderedList,
      isActive: blockType === "ol",
      command: INSERT_ORDERED_LIST_COMMAND,
    },
  ];

  return (
    <>
      {BLOCK_FORMATS.map(({ icon, aria, isActive, command }) => (
        <ToolbarButton
          icon={icon}
          key={aria}
          onClick={() => formatList(isActive, command)}
          aria-label={t(aria)}
          isActive={isActive}
        />
      ))}
    </>
  );
};

export { BlockFormatButtons };
